import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Paper,
  Switch,
  FormControlLabel,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Radio,
  Popover,
  ClickAwayListener,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  FileCopy as FileCopyIcon,
  Link as LinkIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  SelectAll as SelectAllIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Schedule as ScheduleIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Today as TodayIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { FormattedMessage, useIntl } from "react-intl";
import { colors, routes } from "../../constants";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { fullToolbarConfig, updateFontSizeLabel, quillStyles } from "../../components/Survey/QuillConfig";
import { useDropzone } from "react-dropzone";
import { saveAs } from "file-saver";
import api from "../../api";
import { FIRST_NAME_TAG, defaultEmailBody } from "./emailDefaults";
import EmailAiActions from "./EmailAiActions";

// Helper function to transform status for display
const transformStatusForDisplay = (status) => {
  if (status?.toLowerCase() === 'completed') {
    return 'LOCKED';
  }
  return status;
};

function CollectorConfigure() {
  const navigate = useNavigate();
  const { surveyId, collectorId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();

  const [collector, setCollector] = useState(null);
  const [collectorName, setCollectorName] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailHeader, setEmailHeader] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [showSurveyButton, setShowSurveyButton] = useState(true);
  const [surveyButtonText, setSurveyButtonText] = useState("");
  const [editingEmail, setEditingEmail] = useState(null);
  const [editingFirstName, setEditingFirstName] = useState(null);
  const [tempEmail, setTempEmail] = useState("");
  const [tempFirstName, setTempFirstName] = useState("");
  const [emailPendingDelete, setEmailPendingDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialFormState, setInitialFormState] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [testEmailDialog, setTestEmailDialog] = useState({ open: false, email: "", firstName: "" });

  // New modal states
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [sendTime, setSendTime] = useState("now");
  const [scheduledDateTime, setScheduledDateTime] = useState(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    return d;
  });
  const [reminderType, setReminderType] = useState("completed");
  const [reminderMessage, setReminderMessage] = useState("");

  // Custom date picker state
  const [pickerAnchorEl, setPickerAnchorEl] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("12:00");
  const [timeView, setTimeView] = useState(false);

  // User selection modal state
  const [userSelectionModal, setUserSelectionModal] = useState(false);
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [lastSelectedIndex, setLastSelectedIndex] = useState(-1);
  const [newlyCreatedUsers, setNewlyCreatedUsers] = useState([]);
  const emailBodyQuillRef = useRef(null);

  // New user creation state
  const [newUserModal, setNewUserModal] = useState(false);
  const [newUserData, setNewUserData] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
  });
  const [creatingUser, setCreatingUser] = useState(false);

  // Inject Quill custom styles
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = quillStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  useEffect(() => {
    console.log('surveyId:', surveyId, 'collectorId:', collectorId); // Debug params
    const fetchCollector = async () => {
      setLoading(true);
      setError(null);
      try {
        const foundCollector = await api.getCollectorById(surveyId, collectorId);
        console.log("API Response:", foundCollector);
        if (foundCollector) {
          setCollector(foundCollector);
          setCollectorName(foundCollector.name || "");
          setEmailRecipients(foundCollector.emailRecipients || []);
          setEmailSubject(
            foundCollector.emailSubject ||
            formatMessage({ id: "collector_configure.subject_default" })
          );
          setEmailHeader(
            foundCollector.emailHeader ||
            formatMessage({ id: "collector_configure.header_default" })
          );
          // A collector with no body of its own starts on the greeting — the same
          // "fall back to the default" rule the subject, header and button text
          // above already follow. A saved body is never rewritten; it just wins.
          // Both this and initialFormState below must use the SAME value, or a
          // freshly-opened collector would read as having unsaved changes.
          const startingEmailBody = foundCollector.emailBody || defaultEmailBody(formatMessage);
          setEmailBody(startingEmailBody);
          setShowSurveyButton(
            foundCollector.showSurveyButton !== undefined
              ? foundCollector.showSurveyButton
              : true
          );
          setSurveyButtonText(
            foundCollector.surveyButtonText ||
            formatMessage({ id: "collector_configure.button_text_default" })
          );
          // Store initial form state for change detection
          setInitialFormState({
            collectorName: foundCollector.name || "",
            emailRecipients: foundCollector.emailRecipients || [],
            emailSubject:
              foundCollector.emailSubject ||
              formatMessage({ id: "collector_configure.subject_default" }),
            emailHeader:
              foundCollector.emailHeader ||
              formatMessage({ id: "collector_configure.header_default" }),
            emailBody: startingEmailBody,
            showSurveyButton:
              foundCollector.showSurveyButton !== undefined
                ? foundCollector.showSurveyButton
                : true,
            surveyButtonText:
              foundCollector.surveyButtonText ||
              formatMessage({ id: "collector_configure.button_text_default" }),
          });
        } else {
          enqueueSnackbar(
            <FormattedMessage id="collector_configure.collector_not_found" />,
            { variant: "error" }
          );
          navigate(`/collector/${surveyId}`, { replace: true });
        }
      } catch (err) {
        console.error("Error fetching collector:", err);
        setError(err.message || formatMessage({ id: "collector_configure.fetch_error" }));
        enqueueSnackbar(
          <FormattedMessage id="collector_configure.fetch_error" />,
          { variant: "error" }
        );
        navigate(`/collector/${surveyId}`, { replace: true });
      } finally {
        setLoading(false);
      }
    };

    if (surveyId && collectorId) {
      fetchCollector();
    } else {
      setError(formatMessage({ id: "collector_configure.fetch_error" }));
      enqueueSnackbar(
        <FormattedMessage id="collector_configure.fetch_error" />,
        { variant: "error" }
      );
      navigate(`/collector/${surveyId || ""}`, { replace: true });
    }
  }, [surveyId, collectorId, navigate, formatMessage, enqueueSnackbar]);

  // Initialize font size label updater when editor is ready
  useEffect(() => {
    const timer = setTimeout(() => {
      if (emailBodyQuillRef.current) {
        const quill = emailBodyQuillRef.current.getEditor();
        if (quill) {
          updateFontSizeLabel(quill);
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [emailBody]);

  // Function to check for unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialFormState) return false;
    const deepEqual = (obj1, obj2) => {
      if (obj1 === obj2) return true;
      if (typeof obj1 !== "object" || typeof obj2 !== "object" || obj1 == null || obj2 == null) {
        return false;
      }
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      if (keys1.length !== keys2.length) return false;
      for (const key of keys1) {
        if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
          return false;
        }
      }
      return true;
    };
    const currentState = {
      collectorName,
      emailRecipients,
      emailSubject,
      emailHeader,
      emailBody,
      showSurveyButton,
      surveyButtonText,
    };
    return !deepEqual(initialFormState, currentState);
  };

  // Handle back to collectors with unsaved changes check
  const handleBackToCollectors = () => {
    if (hasUnsavedChanges()) {
      setOpenDialog(true);
    } else {
      navigate(`/collector/${surveyId}`, { replace: true });
    }
  };

  // Handle dialog actions
  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleSaveAndNavigate = async () => {
    setLoading(true);
    setError(null);
    try {
      const transformedEmailRecipients = (emailRecipients || []).map(recipient => ({
        email: recipient.email,
        first_name: recipient.firstName || "",
        is_valid: recipient.isValid !== undefined ? recipient.isValid : true
      }));
      const updatedCollector = {
        survey_id: parseInt(surveyId),
        name: collectorName,
        email_recipients: transformedEmailRecipients,
        email_subject: emailSubject,
        email_header: emailHeader,
        email_body: emailBody,
        show_survey_button: showSurveyButton,
        survey_button_text: surveyButtonText,
      };
      console.log(updatedCollector);
      await api.updateCollector(surveyId, collectorId, updatedCollector);
      enqueueSnackbar(<FormattedMessage id="collector_configure.save_success" />, {
        variant: "success",
      });
      setOpenDialog(false);
      navigate(`/collector/${surveyId}`, { replace: true });
    } catch (err) {
      console.error("Error updating collector:", err);
      setError(err.message || formatMessage({ id: "collector_configure.save_error" }));
      enqueueSnackbar(<FormattedMessage id="collector_configure.save_error" />, {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDiscardAndNavigate = () => {
    setOpenDialog(false);
    navigate(`/collector/${surveyId}`, { replace: true });
  };

  const handleAddEmail = () => {
    if (!emailInput) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(emailInput);
    if (!isValid) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.invalid_email" />, {
        variant: "error",
      });
      return;
    }
    if (emailRecipients.some((recipient) => recipient.email === emailInput)) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.email_exists" />, {
        variant: "warning",
      });
      return;
    }
    setEmailRecipients([...emailRecipients, { email: emailInput, firstName: "", isValid: true }]);
    setEmailInput("");
  };

  const handleDeleteEmail = (emailToDelete) => {
    setEmailRecipients(emailRecipients.filter((recipient) => recipient.email !== emailToDelete));
    setEmailPendingDelete(null);
  };

  const handleConfirmDeleteEmail = (email) => {
    setEmailPendingDelete(email);
  };

  const handleCancelDeleteEmail = () => {
    setEmailPendingDelete(null);
  };

  const handleCopyEmail = (email) => {
    navigator.clipboard.writeText(email);
    enqueueSnackbar(<FormattedMessage id="collector_configure.email_copied" />, {
      variant: "success",
    });
  };

  const handleCopySurveyLink = (email) => {
    if (!email) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.no_email_for_link" />, {
        variant: "error",
      });
      return;
    }
    const baseUrl = `https://survey.example.com/survey/${collectorId}`;
    const link = `${baseUrl}?email=${encodeURIComponent(email)}`;
    navigator.clipboard.writeText(link);
    enqueueSnackbar(
      <FormattedMessage id="collector_configure.survey_link_copied" values={{ email }} />,
      { variant: "success" }
    );
  };

  const handleEditEmail = (recipient) => {
    setEditingEmail(recipient.email);
    setTempEmail(recipient.email);
  };

  const handleEditFirstName = (recipient) => {
    console.log("handleEditFirstName called.")
    setEditingFirstName(recipient.email);
    setTempFirstName(recipient.firstName || "");
  };

  const handleUpdateEmail = (oldEmail) => {
    if (!tempEmail) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.email_empty" />, {
        variant: "error",
      });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(tempEmail);
    if (!isValid) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.invalid_email" />, {
        variant: "error",
      });
      return;
    }
    if (emailRecipients.some((recipient) => recipient.email === tempEmail && recipient.email !== oldEmail)) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.email_exists" />, {
        variant: "warning",
      });
      return;
    }
    setEmailRecipients(
      emailRecipients.map((recipient) =>
        recipient.email === oldEmail ? { ...recipient, email: tempEmail, isValid: true } : recipient
      )
    );
    setEditingEmail(null);
    setTempEmail("");
  };

  const handleUpdateFirstName = (email) => {
    console.log("handleUpdateFirstName called.")
    console.log(tempFirstName)
    setEmailRecipients(
      emailRecipients.map((recipient) =>
        recipient.email === email ? { ...recipient, firstName: tempFirstName } : recipient
      )
    );
    setEditingFirstName(null);
    setTempFirstName("");
  };

  const handleDownloadEmails = () => {
    if (emailRecipients.length === 0) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.no_emails_to_download" />, {
        variant: "warning",
      });
      return;
    }
    const headers = ["email", "firstName"];
    const csvRows = [
      headers.join(","),
      ...emailRecipients.map((recipient) =>
        `"${recipient.email}","${recipient.firstName || ""}"`
      ),
    ];
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `email_recipients_${collectorId}.csv`);
    enqueueSnackbar(<FormattedMessage id="collector_configure.emails_downloaded" />, {
      variant: "success",
    });
  };

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const allowedTypes = ["text/csv"];
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.invalid_file_type" />, {
        variant: "error",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split("\n").filter((line) => line.trim() !== "");
      if (lines.length === 0) {
        enqueueSnackbar(<FormattedMessage id="collector_configure.no_data_in_file" />, {
          variant: "error",
        });
        return;
      }

      const headers = lines[0].split(",").map((header) => header.trim().toLowerCase());
      const emailIndex = headers.indexOf("email");
      const firstNameIndex = headers.indexOf("firstname");

      if (emailIndex === -1) {
        enqueueSnackbar(<FormattedMessage id="collector_configure.missing_email_column" />, {
          variant: "error",
        });
        return;
      }

      const newRecipients = [];
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      for (let i = 1; i < lines.length; i++) {
        const row = lines[i].split(",").map((cell) => cell.trim());
        const email = row[emailIndex] || "";
        if (!email) continue;

        const isValid = emailRegex.test(email);
        const firstName = firstNameIndex !== -1 ? row[firstNameIndex] || "" : "";
        newRecipients.push({ email, firstName, isValid });
      }

      const existingEmails = new Set(emailRecipients.map((recipient) => recipient.email));
      const validNewRecipients = newRecipients.filter((recipient) => !existingEmails.has(recipient.email));
      const invalidEmails = validNewRecipients.filter((recipient) => !recipient.isValid);

      setEmailRecipients([...emailRecipients, ...validNewRecipients]);

      if (validNewRecipients.length > 0) {
        enqueueSnackbar(
          <FormattedMessage id="collector_configure.emails_added_success" values={{ count: validNewRecipients.length }} />,
          { variant: "success" }
        );
      }
      if (invalidEmails.length > 0) {
        enqueueSnackbar(
          <FormattedMessage
            id="collector_configure.invalid_emails"
            values={{
              count: invalidEmails.length,
              emails: invalidEmails.map((r) => r.email).join(", "),
            }}
          />,
          { variant: "warning" }
        );
      }
      if (validNewRecipients.length === 0 && invalidEmails.length === 0) {
        enqueueSnackbar(<FormattedMessage id="collector_configure.no_new_email" />, { variant: "info" });
      }
    };

    reader.onerror = () => {
      enqueueSnackbar(<FormattedMessage id="collector_configure.file_read_error" />, { variant: "error" });
    };

    reader.readAsText(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    multiple: false,
  });

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const transformedEmailRecipients = (emailRecipients || []).map(recipient => ({
        email: recipient.email,
        first_name: recipient.firstName || "",
        is_valid: recipient.isValid !== undefined ? recipient.isValid : true
      }));

      console.log(transformedEmailRecipients);
      const updatedCollector = {
        survey_id: parseInt(surveyId),
        name: collectorName,
        email_recipients: transformedEmailRecipients,
        email_subject: emailSubject,
        email_header: emailHeader,
        email_body: emailBody,
        show_survey_button: showSurveyButton,
        survey_button_text: surveyButtonText,
        action: "save",
      };
      console.log(updatedCollector);
      await api.updateCollector(surveyId, collectorId, updatedCollector);
      enqueueSnackbar(<FormattedMessage id="collector_configure.save_success" />, {
        variant: "success",
      });
      setInitialFormState({
        collectorName,
        emailRecipients,
        emailSubject,
        emailHeader,
        emailBody,
        showSurveyButton,
        surveyButtonText,
      });
    } catch (err) {
      console.error("Error updating collector:", err);
      setError(err.message || formatMessage({ id: "collector_configure.save_error" }));
      enqueueSnackbar(<FormattedMessage id="collector_configure.save_error" />, {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetTimeAndSend = () => {
    setSendModalOpen(true);
  };

  const handleReminder = () => {
    setReminderModalOpen(true);
    // Set default message based on reminder type
    setReminderMessage(getDefaultReminderMessage("completed"));
  };

  const handleSendModalClose = () => {
    setSendModalOpen(false);
    setSendTime("now");
    setScheduledDateTime(new Date());
  };

  // Formats a Date using its LOCAL date/time parts so the selected wall-clock
  // time is preserved verbatim — the backend interprets the value itself.
  const toLocalISOString = (date) => {
    const p = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}:00Z`;
  };

  const handleReminderModalClose = () => {
    setReminderModalOpen(false);
    setReminderType("completed");
    setReminderMessage("");
  };

  const getDefaultReminderMessage = (type) => {
    if (type === "completed") {
      return `<p>Dear <strong>$recipient_name$</strong>,</p>

<p>We noticed that you haven't completed the "<strong>$survey_name$</strong>" assessment yet. Your feedback is very important to us and will help us improve our services.</p>

<p>Please take a few minutes to complete the assessment by clicking the link below:</p>

<p><a href="$survey_link$" style="background-color: #1976d2; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: 600;">Complete Assessment</a></p>

<p>If you have already completed the assessment or if you have any questions, please don't hesitate to contact us.</p>

<p>Thank you for your time and valuable feedback!</p>

<p>Best regards,<br/>The Prompta Team</p>`;
    } else if (type === "note") {
      return `<p>Dear <strong>$recipient_name$</strong>,</p>

<p>Thank you for taking the time to complete the "<strong>$survey_name$</strong>" assessment! We truly appreciate your feedback and participation.</p>

<p>Your responses have been recorded and will help us improve our services. We value your input and are grateful for your contribution to our research.</p>

<p>If you have any additional comments or suggestions, please feel free to share them with us.</p>

<p>Thank you once again for your participation!</p>

<p>Best regards,<br/>The Prompta Team</p>`;
    }
    return "";
  };

  // Custom date picker functions
  const handlePickerOpen = (event) => {
    setPickerAnchorEl(event.currentTarget);
    setPickerOpen(true);
    setSelectedDate(new Date(scheduledDateTime));
    const timeString = scheduledDateTime.toTimeString().slice(0, 5);
    setSelectedTime(timeString);
    setTimeView(false);
  };

  const handlePickerClose = () => {
    setPickerOpen(false);
    setPickerAnchorEl(null);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setTimeView(true);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handlePickerConfirm = () => {
    const [hours, minutes] = selectedTime.split(':');
    const newDateTime = new Date(selectedDate);
    newDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    // Ensure the selected time is in the future
    const now = new Date();
    if (newDateTime <= now) {
      enqueueSnackbar(
        <FormattedMessage id="collector_configure.future_time_required" defaultMessage="Please select a future date and time" />,
        { variant: "warning" }
      );
      return;
    }

    setScheduledDateTime(newDateTime);
    handlePickerClose();
  };

  const generateTimeSlots = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 5) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        times.push({ value: timeString, display: displayTime });
      }
    }
    return times;
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const handleSendInvitations = async (isScheduled = false, scheduledTime = null) => {
    if (emailRecipients.length === 0) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.send_no_recipients" />, {
        variant: "error",
      });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const transformedEmailRecipients = (emailRecipients || []).map(recipient => ({
        email: recipient.email,
        first_name: recipient.firstName || "",
        is_valid: recipient.isValid !== undefined ? recipient.isValid : true
      }));

      const sendData = {
        survey_id: parseInt(surveyId),
        name: collectorName,
        email_recipients: transformedEmailRecipients,
        email_subject: emailSubject,
        email_header: emailHeader,
        email_body: emailBody,
        show_survey_button: showSurveyButton,
        survey_button_text: surveyButtonText,
        action: isScheduled ? "schedule" : "collect",
      };

      if (isScheduled && scheduledTime) {
        sendData.scheduled_time = scheduledTime;
      }

      await api.updateCollector(surveyId, collectorId, sendData);

      if (isScheduled) {
        enqueueSnackbar(<FormattedMessage id="collector_configure.schedule_success" />, {
          variant: "success",
        });
      } else {
        enqueueSnackbar(<FormattedMessage id="collector_configure.invitations_sent" />, {
          variant: "success",
        });
      }

      handleSendModalClose();
      navigate(`/collector/${surveyId}`, { replace: true });
    } catch (err) {
      console.error("Error sending invitations:", err);
      setError(err.message || formatMessage({ id: "collector_configure.send_error" }));
      enqueueSnackbar(<FormattedMessage id="collector_configure.send_error" />, {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // const handleTest = async () => {
  //   if (emailRecipients.length === 0) {
  //     enqueueSnackbar(<FormattedMessage id="collector_configure.test_no_recipients" />, {
  //       variant: "error",
  //     });
  //     return;
  //   }
  //   if (!fromEmail) {
  //     enqueueSnackbar(<FormattedMessage id="collector_configure.no_from_email" />, {
  //       variant: "error",
  //     });
  //     return;
  //   }
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     const transformedEmailRecipients = (emailRecipients || []).map(recipient => ({
  //       email: recipient.email,
  //       first_name: recipient.firstName || "",
  //       is_valid: recipient.isValid !== undefined ? recipient.isValid : true
  //     }));
  //     const testData = {
  //       survey_id: parseInt(surveyId),
  //       name: collectorName,
  //       email_recipients: transformedEmailRecipients,
  //       from_email: fromEmail,
  //       email_subject: emailSubject,
  //       email_header: emailHeader,
  //       email_body: emailBody,
  //       show_survey_button: showSurveyButton,
  //       survey_button_text: surveyButtonText,
  //       action: "test",
  //     };
  //     await api.updateCollector(surveyId, collectorId, testData);
  //     enqueueSnackbar(
  //       <FormattedMessage
  //         id="collector_configure.test_email_sent"
  //         values={{ recipient: emailRecipients[0].email, fromEmail }}
  //       />,
  //       { variant: "success" }
  //     );
  //   } catch (err) {
  //     console.error("Error sending test email:", err);
  //     setError(err.message || formatMessage({ id: "collector_configure.test_error" }));
  //     enqueueSnackbar(<FormattedMessage id="collector_configure.test_error" />, {
  //       variant: "error",
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleTest = () => {
    setTestEmailDialog({ open: true, email: "", firstName: "" });
  };

  const handleTestEmailSubmit = async () => {
    const { email, firstName } = testEmailDialog;
    if (!email) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.test_email_empty" />, {
        variant: "error",
      });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.invalid_email" />, {
        variant: "error",
      });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const testData = {
        survey_id: parseInt(surveyId),
        name: collectorName,
        email_recipients: [{ email, first_name: firstName, is_valid: true }],
        email_subject: emailSubject,
        email_header: emailHeader,
        email_body: emailBody,
        show_survey_button: showSurveyButton,
        survey_button_text: surveyButtonText,
        action: "test_email",
        test_email: email,
        test_first_name: firstName,
      };
      console.log("Test Email Request:", testData);
      await api.updateCollector(surveyId, collectorId, testData);
      enqueueSnackbar(
        <FormattedMessage
          id="collector_configure.test_email_sent"
          values={{ recipient: email }}
        />,
        { variant: "success" }
      );
      setTestEmailDialog({ open: false, email: "", firstName: "" });
    } catch (err) {
      console.error("Error sending test email:", err);
      setError(err.message || formatMessage({ id: "collector_configure.test_error" }));
      enqueueSnackbar(<FormattedMessage id="collector_configure.test_error" />, {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmailDialogClose = () => {
    setTestEmailDialog({ open: false, email: "", firstName: "" });
  };
  // Original handleSchedule function - no longer used (replaced by modal-based scheduling)
  const handleScheduleOriginal = async () => {
    if (emailRecipients.length === 0) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.schedule_no_recipients" />, {
        variant: "error",
      });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const transformedEmailRecipients = (emailRecipients || []).map(recipient => ({
        email: recipient.email,
        first_name: recipient.firstName || "",
        is_valid: recipient.isValid !== undefined ? recipient.isValid : true
      }));
      const scheduleData = {
        survey_id: parseInt(surveyId),
        name: collectorName,
        email_recipients: transformedEmailRecipients,
        email_subject: emailSubject,
        email_header: emailHeader,
        email_body: emailBody,
        show_survey_button: showSurveyButton,
        survey_button_text: surveyButtonText,
        action: "schedule",
      };
      await api.updateCollector(surveyId, collectorId, scheduleData);
      enqueueSnackbar(<FormattedMessage id="collector_configure.schedule_success" />, {
        variant: "success",
      });
    } catch (err) {
      console.error("Error scheduling collector:", err);
      setError(err.message || formatMessage({ id: "collector_configure.schedule_error" }));
      enqueueSnackbar(<FormattedMessage id="collector_configure.schedule_error" />, {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };


  const handleCopyLink = () => {
    if (collector && collector.link) {
      const fullUrl = `${window.location.origin}${collector.link}`;
      navigator.clipboard.writeText(fullUrl);
      enqueueSnackbar(<FormattedMessage id="collector_configure.link_copied" />, {
        variant: "success",
      });
    } else {
      enqueueSnackbar(<FormattedMessage id="collector_configure.no_link" />, {
        variant: "error",
      });
    }
  };

  const handleCopyEmbedCode = () => {
    if (collector && collector.embedCode) {
      navigator.clipboard.writeText(collector.embedCode);
      enqueueSnackbar(<FormattedMessage id="collector_configure.embed_copied" />, {
        variant: "success",
      });
    } else {
      enqueueSnackbar(<FormattedMessage id="collector_configure.no_embed_code" />, {
        variant: "error",
      });
    }
  };

  const handleBackToCollectorList = () => {
    if (hasUnsavedChanges()) {
      setOpenDialog(true);
    } else {
      navigate(`/collector/${surveyId}`, { replace: true });
    }
  };

  // User Selection Modal Functions
  const handleOpenUserSelectionModal = async () => {
    setUserSelectionModal(true);
    setUsersLoading(true);
    setSelectedUsers([]);
    setUserSearchQuery("");
    setLastSelectedIndex(-1);

    try {
      const response = await api.getOrganizationUsers();
      if (response.success && response.data) {
        setOrganizationUsers(response.data);
      } else {
        enqueueSnackbar(<FormattedMessage id="collector_configure.fetch_users_error" defaultMessage="Failed to fetch organization users" />, {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching organization users:", error);
      enqueueSnackbar(<FormattedMessage id="collector_configure.fetch_users_error" defaultMessage="Failed to fetch organization users" />, {
        variant: "error",
      });
    } finally {
      setUsersLoading(false);
    }
  };

  const handleCloseUserSelectionModal = () => {
    setUserSelectionModal(false);
    setNewUserModal(false);
    setSelectedUsers([]);
    setUserSearchQuery("");
    setLastSelectedIndex(-1);
  };

  const handleUserSelection = (user, index, event) => {
    const { ctrlKey, shiftKey } = event;
    let newSelectedUsers = [...selectedUsers];

    if (shiftKey && lastSelectedIndex !== -1) {
      // Range selection with Shift
      const startIndex = Math.min(lastSelectedIndex, index);
      const endIndex = Math.max(lastSelectedIndex, index);
      const visibleUsers = filteredUsers;

      for (let i = startIndex; i <= endIndex; i++) {
        const userToSelect = visibleUsers[i];
        if (userToSelect && !newSelectedUsers.some(u => u.id === userToSelect.id)) {
          newSelectedUsers.push(userToSelect);
        }
      }
    } else if (ctrlKey) {
      // Toggle selection with Ctrl
      const isSelected = newSelectedUsers.some(u => u.id === user.id);
      if (isSelected) {
        newSelectedUsers = newSelectedUsers.filter(u => u.id !== user.id);
      } else {
        newSelectedUsers.push(user);
      }
    } else {
      // Single selection
      newSelectedUsers = [user];
    }

    setSelectedUsers(newSelectedUsers);
    setLastSelectedIndex(index);
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers([...filteredUsers]);
    }
  };

  const handleAddSelectedUsers = () => {
    const newRecipients = selectedUsers.map(user => ({
      email: user.email,
      firstName: user.first_name || "",
      isValid: true
    })).filter(recipient =>
      !emailRecipients.some(existing => existing.email === recipient.email)
    );

    if (newRecipients.length > 0) {
      setEmailRecipients([...emailRecipients, ...newRecipients]);
      enqueueSnackbar(
        <FormattedMessage
          id="collector_configure.users_added"
          defaultMessage="{count} user(s) added to recipients"
          values={{ count: newRecipients.length }}
        />,
        { variant: "success" }
      );
    }

    handleCloseUserSelectionModal();
  };

  // New User Creation Functions
  const handleOpenNewUserModal = () => {
    setNewUserModal(true);
    setNewUserData({
      first_name: "",
      last_name: "",
      username: "",
      email: "",
    });
  };

  const handleCloseNewUserModal = () => {
    setNewUserModal(false);
    setNewUserData({
      first_name: "",
      last_name: "",
      username: "",
      email: "",
    });
  };

  const handleNewUserInputChange = (field, value) => {
    setNewUserData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateNewUser = async () => {
    if (!newUserData.email) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.email_required" defaultMessage="Email is required" />, {
        variant: "error",
      });
      return;
    }

    setCreatingUser(true);
    try {
      const response = await api.addOrganizationUser({
        first_name: newUserData.first_name,
        last_name: newUserData.last_name,
        username: newUserData.username,
        email: newUserData.email,
      });

      if (response.success) {
        // Add the new user to recipients
        const newRecipient = {
          email: newUserData.email,
          firstName: newUserData.first_name || "",
          isValid: true
        };

        setEmailRecipients([...emailRecipients, newRecipient]);
        enqueueSnackbar(<FormattedMessage id="collector_configure.user_created_and_added" defaultMessage="User created and added to recipients" />, {
          variant: "success",
        });
        handleCloseNewUserModal();
      } else {
        enqueueSnackbar(response.error || <FormattedMessage id="collector_configure.user_creation_failed" defaultMessage="Failed to create user" />, {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error creating user:", error);
      enqueueSnackbar(<FormattedMessage id="collector_configure.user_creation_failed" defaultMessage="Failed to create user" />, {
        variant: "error",
      });
    } finally {
      setCreatingUser(false);
    }
  };

  const handleCreateUserOnly = async () => {
    if (!newUserData.email) {
      enqueueSnackbar(<FormattedMessage id="collector_configure.email_required" defaultMessage="Email is required" />, {
        variant: "error",
      });
      return;
    }

    setCreatingUser(true);
    try {
      const response = await api.addOrganizationUser({
        first_name: newUserData.first_name,
        last_name: newUserData.last_name,
        username: newUserData.username,
        email: newUserData.email,
      });

      if (response.success) {
        // Add the new user to organization users list and refresh the list
        const newUser = {
          id: Date.now(), // Temporary ID, should be returned from API
          first_name: newUserData.first_name,
          last_name: newUserData.last_name,
          username: newUserData.username,
          email: newUserData.email,
        };

        setOrganizationUsers(prev => [...prev, newUser]);
        setNewlyCreatedUsers(prev => [...prev, newUser.id]);

        // Remove highlight after 3 seconds
        setTimeout(() => {
          setNewlyCreatedUsers(prev => prev.filter(id => id !== newUser.id));
        }, 3000);

        enqueueSnackbar(<FormattedMessage id="collector_configure.user_created_only" defaultMessage="User created successfully" />, {
          variant: "success",
        });

        // Reset form but keep modal open
        setNewUserData({
          first_name: "",
          last_name: "",
          username: "",
          email: "",
        });
      } else {
        enqueueSnackbar(response.error || <FormattedMessage id="collector_configure.user_creation_failed" defaultMessage="Failed to create user" />, {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error creating user:", error);
      enqueueSnackbar(<FormattedMessage id="collector_configure.user_creation_failed" defaultMessage="Failed to create user" />, {
        variant: "error",
      });
    } finally {
      setCreatingUser(false);
    }
  };

  // Filtered users for search
  const filteredUsers = organizationUsers.filter((user) => {
    const searchLower = userSearchQuery.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      (user.first_name && user.first_name.toLowerCase().includes(searchLower)) ||
      (user.last_name && user.last_name.toLowerCase().includes(searchLower)) ||
      (user.username && user.username.toLowerCase().includes(searchLower))
    );
  });

  const filteredEmailRecipients = emailRecipients.filter((recipient) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      recipient.email.toLowerCase().includes(searchLower) ||
      (recipient.firstName && recipient.firstName.toLowerCase().includes(searchLower))
    );
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!collector) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "32px",
          minHeight: "calc(100vh - 64px)",
          display: "flex",
          flexDirection: "column",
          pt: 6,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <Tooltip title={<FormattedMessage id="nav.back_to_collector_list" />}>
            <IconButton onClick={handleBackToCollectorList} sx={{ color: colors.main, mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: colors.main, mr: 2 }}>
            <FormattedMessage id="collector_configure.header" />
          </Typography>
          <TextField
            value={collectorName}
            onChange={(e) => setCollectorName(e.target.value)}
            variant="outlined"
            placeholder={formatMessage({ id: "collector.enter_name_placeholder" })}
            sx={{
              width: "300px",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius: "8px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  transform: "translateY(-1px)"
                },
                "&.Mui-focused": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  boxShadow: "0 4px 16px rgba(25, 118, 210, 0.2)",
                  transform: "translateY(-2px)"
                }
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(0, 0, 0, 0.2)",
                borderWidth: "1px",
                transition: "all 0.3s ease"
              },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(25, 118, 210, 0.5)",
                borderWidth: "2px"
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1976d2",
                borderWidth: "2px"
              },
              "& .MuiInputBase-input": {
                padding: "12px 16px",
                fontSize: "0.95rem",
                fontWeight: "500",
                color: "#333"
              },
              "& .MuiInputLabel-root": {
                color: "rgba(0, 0, 0, 0.6)",
                fontWeight: "500"
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#1976d2",
                fontWeight: "600"
              }
            }}
          />
        </Box>

        {collector.type === "EMAIL" && (
          <Card
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              borderRadius: "16px",
              boxShadow: "0 8px 32px rgba(0, 67, 142, 0.1)",
              border: "1px solid rgba(0, 67, 142, 0.1)",
              overflow: "hidden",
              mb: 4
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  display: { xs: 'block', md: 'flex' },
                  minHeight: '600px'
                }}
              >
                {/* Left Column - Email Content */}
                <Box
                  sx={{
                    flex: 1,
                    p: 3,
                    borderRight: { xs: 'none', md: '1px solid rgba(0, 67, 142, 0.1)' },
                    borderBottom: { xs: '1px solid rgba(0, 67, 142, 0.1)', md: 'none' },
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 3, color: colors.main, fontWeight: "bold" }}>
                    <FormattedMessage id="collector_configure.email_content" />
                  </Typography>

                  <EmailAiActions
                    surveyId={surveyId}
                    collectorId={collectorId}
                    subject={emailSubject}
                    body={emailBody}
                    defaultBody={defaultEmailBody(formatMessage)}
                    showSurveyButton={showSurveyButton}
                    surveyButtonText={surveyButtonText}
                    onApply={({ subject, body }) => {
                      setEmailSubject(subject);
                      setEmailBody(body);
                    }}
                  />

                  <TextField
                    label={<FormattedMessage id="collector_configure.subject" />}
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    fullWidth
                    sx={{ mb: 3 }}
                  />


                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: "500" }}>
                      <FormattedMessage id="collector_configure.email_body" />
                    </Typography>
                    {/* Sits ABOVE the editor on purpose: the Quill box is a fixed
                        280px and overflows this container, so anything placed under
                        it lands on top of the editor. */}
                    <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
                      <FormattedMessage
                        id="collector_configure.merge_tag_hint"
                        defaultMessage="{name} is replaced with each recipient's first name, or “there” when we don't have one."
                        values={{
                          name: (
                            <Box component="code" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                              {FIRST_NAME_TAG}
                            </Box>
                          ),
                        }}
                      />
                    </Typography>
                    <ReactQuill
                      ref={emailBodyQuillRef}
                      value={emailBody}
                      onChange={setEmailBody}
                      placeholder={formatMessage({ id: "collector_configure.email_body_placeholder" })}
                      modules={fullToolbarConfig}
                      formats={[
                        "bold",
                        "italic",
                        "underline",
                        "color",
                        "size",
                        "lineheight",
                        "letterspacing",
                        "align",
                        "link",
                        "image",
                        "list",
                        "bullet",
                      ]}
                      style={{
                        backgroundColor: "#ffffff",
                        borderRadius: "20px",
                        height: "280px",
                      }}
                    />
                  </Box>

                  <Box sx={{ flexDirection: "row", mt: { xs: 10, md: 7 }, display: "flex", alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showSurveyButton}
                          onChange={(e) => setShowSurveyButton(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={<FormattedMessage id="collector_configure.show_button" />}
                      sx={{ mb: 2 }}
                    />
                    {showSurveyButton && (
                      <TextField
                        label={<FormattedMessage id="collector_configure.button_text_label" />}
                        value={surveyButtonText}
                        onChange={(e) => setSurveyButtonText(e.target.value)}
                        fullWidth
                      />
                    )}
                  </Box>
                </Box>

                {/* Right Column - Recipients Section */}
                <Box
                  sx={{
                    flex: 1,
                    p: 3,
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease",
                  }}
                >

                  <Typography variant="h6" sx={{ mb: 3, color: colors.main, fontWeight: "bold" }}>
                    <FormattedMessage id="collector_configure.email_recipients" />
                  </Typography>

                  <Box sx={{ display: "flex", gap: 2, mb: 3, flexDirection: "row" }}>
                    <TextField
                      label={<FormattedMessage id="collector_configure.email_label" />}
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      // fullWidth
                      placeholder={formatMessage({ id: "collector_configure.email_placeholder" })}
                      sx={{ minWidth: { xs: "100%", sm: "80%" } }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleAddEmail}
                      sx={{ textTransform: "none", borderRadius: "10px" }}
                    >
                      <FormattedMessage id="collector_configure.add_button" />
                    </Button>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      label={<FormattedMessage id="collector_configure.search_emails" />}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        endAdornment: <SearchIcon />,
                      }}
                      sx={{
                        width: "30%",
                        "& .MuiInputBase-input": {
                          fontSize: "0.7rem",
                          padding: "8px 16px"
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.7rem"
                        }
                      }}
                    />
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 1 }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadEmails}
                        sx={{ textTransform: "none", borderRadius: "20px", fontSize: "0.7rem" }}
                      >
                        <FormattedMessage id="collector_configure.download_emails" />
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<PeopleIcon />}
                        onClick={handleOpenUserSelectionModal}
                        sx={{ textTransform: "none", borderRadius: "20px", fontSize: "0.7rem" }}
                      >
                        <FormattedMessage id="collector_configure.add_from_users" defaultMessage="Add from Users" />
                      </Button>
                    </Box>
                  </Box>

                  <Box sx={{ flex: 1, mb: 3 }}>
                    {emailRecipients.length === 0 ? (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography sx={{ color: "text.secondary", mb: 2 }}>
                          <FormattedMessage id="collector_configure.email_list_empty" />
                        </Typography>
                      </Box>
                    ) : (
                      <>

                        <TableContainer sx={{ maxHeight: "300px", overflowY: "auto" }}>
                          <Table stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>
                                  <FormattedMessage id="collector_configure.email_label" />
                                </TableCell>
                                <TableCell>
                                  <FormattedMessage id="collector_configure.first_name" />
                                </TableCell>
                                <TableCell align="right">
                                  <FormattedMessage id="collector_configure.actions" />
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredEmailRecipients.map((recipient, index) => {
                                const isPendingDelete = emailPendingDelete === recipient.email;
                                return (
                                  <TableRow
                                    key={index}
                                    sx={
                                      isPendingDelete
                                        ? {
                                          backgroundColor: "rgba(211, 47, 47, 0.12)",
                                          "&:hover": {
                                            backgroundColor: "rgba(211, 47, 47, 0.18)",
                                          },
                                        }
                                        : undefined
                                    }
                                  >
                                    <TableCell sx={{ color: recipient.isValid ? "inherit" : "red" }}>
                                      {editingEmail === recipient.email ? (
                                        <TextField
                                          value={tempEmail}
                                          onChange={(e) => setTempEmail(e.target.value)}
                                          onBlur={() => handleUpdateEmail(recipient.email)}
                                          onKeyPress={(e) => {
                                            if (e.key === "Enter") {
                                              handleUpdateEmail(recipient.email);
                                            }
                                          }}
                                          autoFocus
                                          size="small"
                                        />
                                      ) : (
                                        <Box
                                          onClick={() => handleEditEmail(recipient)}
                                          sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                                        >
                                          {recipient.email}
                                        </Box>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {editingFirstName === recipient.email ? (
                                        <TextField
                                          value={tempFirstName}
                                          onChange={(e) => setTempFirstName(e.target.value)}
                                          onBlur={() => handleUpdateFirstName(recipient.email)}
                                          onKeyPress={(e) => {
                                            if (e.key === "Enter") {
                                              handleUpdateFirstName(recipient.email);
                                            }
                                          }}
                                          autoFocus
                                          size="small"
                                        />
                                      ) : (
                                        <Box
                                          onClick={() => handleEditFirstName(recipient)}
                                          sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                                        >
                                          {recipient.firstName || <FormattedMessage id="collector_configure.first_name_placeholder" />}
                                        </Box>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {isPendingDelete ? (
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleCancelDeleteEmail}
                                            sx={{ textTransform: "none", minWidth: "auto" }}
                                          >
                                            <FormattedMessage id="collector_configure.cancel" />
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="error"
                                            onClick={() => handleDeleteEmail(recipient.email)}
                                            sx={{ textTransform: "none", minWidth: "auto" }}
                                          >
                                            <FormattedMessage id="collector_configure.delete_confirm_ok" defaultMessage="Delete" />
                                          </Button>
                                        </Box>
                                      ) : (
                                        <>
                                          <Tooltip title={<FormattedMessage id="collector_configure.copy_email" />}>
                                            <IconButton
                                              onClick={() => handleCopyEmail(recipient.email)}
                                              size="small"
                                              sx={{ mr: 1 }}
                                            >
                                              <FileCopyIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title={<FormattedMessage id="collector_configure.delete_email" />}>
                                            <IconButton
                                              onClick={() => handleConfirmDeleteEmail(recipient.email)}
                                              size="small"
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </Box>

                  {/* Droppable Area */}
                  <Box
                    {...getRootProps()}
                    sx={{
                      mt: "auto",
                      height: "60px",
                      backgroundColor: isDragActive
                        ? "rgba(25, 118, 210, 0.1)"
                        : "transparent",
                      border: isDragActive
                        ? "2px solid #1976d2"
                        : "2px dashed transparent",
                      borderRadius: isDragActive ? "0 0 8px 8px" : "0",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: isDragActive ? 10 : 1,
                      "&:hover": {
                        backgroundColor: isDragActive
                          ? "rgba(25, 118, 210, 0.15)"
                          : "rgba(0, 67, 142, 0.05)",
                        borderColor: isDragActive ? "#1976d2" : "rgba(0, 67, 142, 0.2)",
                      },
                    }}
                  >
                    <input {...getInputProps()} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDragActive ? "#1976d2" : "rgba(0, 67, 142, 0.6)",
                        fontSize: "0.75rem",
                        textAlign: "center",
                        fontWeight: isDragActive ? "bold" : "normal",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <FormattedMessage
                        id="collector_configure.drop_zone_hint"
                        defaultMessage="Click to select or drag and drop your .csv file here"
                      />
                    </Typography>

                    {isDragActive && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: "rgba(25, 118, 210, 0.1)",
                          backdropFilter: "blur(2px)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 10,
                          borderRadius: "0 0 8px 8px",
                        }}
                      >
                        <Box sx={{ textAlign: "center" }}>
                          <DownloadIcon sx={{ fontSize: 32, color: "#1976d2", mb: 1 }} />
                          <Typography variant="body2" sx={{ color: "#1976d2", fontWeight: "bold" }}>
                            <FormattedMessage id="collector_configure.drop_file_import" defaultMessage="Drop the file to import recipients" />
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleSave}
                  sx={{ textTransform: "none", borderRadius: "20px", px: 3 }}
                  disabled={loading || !collectorName}
                >
                  <FormattedMessage id="collector_configure.save_button" />
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSetTimeAndSend}
                  sx={{ textTransform: "none", borderRadius: "20px", px: 3 }}
                  disabled={loading || !collectorName}
                >
                  <FormattedMessage id="collector_configure.save_and_send" />
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {collector.type === "LINK" && (
          <Card
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              borderRadius: "16px",
              boxShadow: "0 8px 32px rgba(76, 175, 80, 0.15)",
              border: "2px solid rgba(76, 175, 80, 0.2)",
              overflow: "hidden",
              mb: 4,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                backgroundColor: "#4caf50",
                borderRadius: "16px 16px 0 0",
              }
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
                <Box
                  sx={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    backgroundColor: "rgba(76, 175, 80, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px solid rgba(76, 175, 80, 0.2)",
                  }}
                >
                  <LinkIcon sx={{ fontSize: "1.5rem", color: "#4caf50" }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: "bold", color: "#2e7d32", mb: 0.5 }}>
                    <FormattedMessage id="collector_configure.survey_link_title" defaultMessage="Assessment Link" />
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <FormattedMessage id="collector_configure.survey_link_description" defaultMessage="Share this link to collect responses" />
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  p: 3,
                  mb: 3,
                  backgroundColor: "rgba(76, 175, 80, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(76, 175, 80, 0.2)",
                  position: "relative",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(76, 175, 80, 0.08)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(76, 175, 80, 0.2)",
                  }
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    wordBreak: "break-all",
                    fontFamily: "'Roboto Mono', monospace",
                    fontSize: "0.95rem",
                    color: "#1b5e20",
                    fontWeight: "500",
                    lineHeight: 1.6,
                    padding: "8px 12px",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    borderRadius: "8px",
                    border: "1px solid rgba(76, 175, 80, 0.15)",
                  }}
                >
                  {collector.link ? `${window.location.origin}${collector.link}` : 'Link not available'}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyLink}
                  sx={{
                    textTransform: "none",
                    borderRadius: "12px",
                    px: 4,
                    py: 1.5,
                    backgroundColor: "#4caf50",
                    color: "#ffffff",
                    fontWeight: "600",
                    fontSize: "1rem",
                    boxShadow: "0 4px 16px rgba(76, 175, 80, 0.3)",
                    "&:hover": {
                      backgroundColor: "#45a049",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(76, 175, 80, 0.4)",
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  <FormattedMessage id="collector_configure.copy_link_button" />
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={() => window.open(`${window.location.origin}${collector.link}`, '_blank')}
                  sx={{
                    textTransform: "none",
                    borderRadius: "12px",
                    px: 4,
                    py: 1.5,
                    borderColor: "#4caf50",
                    color: "#4caf50",
                    fontWeight: "600",
                    fontSize: "1rem",
                    borderWidth: "2px",
                    "&:hover": {
                      backgroundColor: "rgba(76, 175, 80, 0.08)",
                      borderColor: "#45a049",
                      color: "#45a049",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(76, 175, 80, 0.2)",
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  <FormattedMessage id="collector_configure.open_link_button" defaultMessage="Open Link" />
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    const fullUrl = `${window.location.origin}${collector.link}`;
                    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(fullUrl)}`;
                    const link = document.createElement('a');
                    link.href = qrCodeUrl;
                    link.download = `survey-qr-code-${collectorId}.png`;
                    link.click();
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: "12px",
                    px: 4,
                    py: 1.5,
                    borderColor: "#4caf50",
                    color: "#4caf50",
                    fontWeight: "600",
                    fontSize: "1rem",
                    borderWidth: "2px",
                    "&:hover": {
                      backgroundColor: "rgba(76, 175, 80, 0.08)",
                      borderColor: "#45a049",
                      color: "#45a049",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(76, 175, 80, 0.2)",
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  <FormattedMessage id="collector_configure.download_qr_button" defaultMessage="Download QR Code" />
                </Button>
              </Box>

            </CardContent>
          </Card>
        )}
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        aria-labelledby="unsaved-changes-dialog-title"
      >
        <DialogTitle id="unsaved-changes-dialog-title">
          <FormattedMessage id="collector_configure.unsaved_changes_title" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage id="collector_configure.unsaved_changes_message" />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            <FormattedMessage id="collector_configure.cancel" />
          </Button>
          <Button onClick={handleDiscardAndNavigate} color="primary">
            <FormattedMessage id="collector_configure.discard" />
          </Button>
          <Button onClick={handleSaveAndNavigate} color="primary" variant="contained" disabled={loading}>
            <FormattedMessage id="collector_configure.save" />
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={testEmailDialog.open}
        onClose={handleTestEmailDialogClose}
        aria-labelledby="test-email-dialog-title"
      >
        <DialogTitle id="test-email-dialog-title">
          <FormattedMessage id="collector_configure.test_email_title" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            <FormattedMessage id="collector_configure.test_email_message" />
          </DialogContentText>
          <TextField
            label={<FormattedMessage id="collector_configure.test_email_label" />}
            value={testEmailDialog.email}
            onChange={(e) => setTestEmailDialog({ ...testEmailDialog, email: e.target.value })}
            fullWidth
            sx={{ mb: 2 }}
            placeholder={formatMessage({ id: "collector_configure.email_placeholder" })}
          />
          <TextField
            label={<FormattedMessage id="collector_configure.test_first_name_label" />}
            value={testEmailDialog.firstName}
            onChange={(e) => setTestEmailDialog({ ...testEmailDialog, firstName: e.target.value })}
            fullWidth
            placeholder={formatMessage({ id: "collector_configure.first_name_placeholder" })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTestEmailDialogClose} color="primary">
            <FormattedMessage id="collector_configure.cancel" />
          </Button>
          <Button
            onClick={handleTestEmailSubmit}
            color="primary"
            variant="contained"
            disabled={loading || !testEmailDialog.email}
          >
            <FormattedMessage id="collector_configure.test_email_send" />
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Selection Modal */}
      <Dialog
        open={userSelectionModal}
        onClose={handleCloseUserSelectionModal}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#00438E',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <PeopleIcon />
          <FormattedMessage id="collector_configure.select_users_title" defaultMessage="Select Users from Organization" />
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3, mt: 3 }}>
            <TextField
              fullWidth
              label={<FormattedMessage id="collector_configure.search_users" defaultMessage="Search users" />}
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />,
              }}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={selectedUsers.length === filteredUsers.length ? <ClearIcon /> : <SelectAllIcon />}
                onClick={handleSelectAllUsers}
                sx={{ textTransform: 'none' }}
              >
                {selectedUsers.length === filteredUsers.length ? (
                  <FormattedMessage id="collector_configure.deselect_all" defaultMessage="Deselect All" />
                ) : (
                  <FormattedMessage id="collector_configure.select_all" defaultMessage="Select All" />
                )}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={handleOpenNewUserModal}
                sx={{ textTransform: 'none' }}
              >
                <FormattedMessage id="collector_configure.create_new_user" defaultMessage="Create New User" />
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <FormattedMessage
                id="collector_configure.selected_count"
                defaultMessage="{count} of {total} users selected"
                values={{ count: selectedUsers.length, total: filteredUsers.length }}
              />
            </Typography>
          </Box>

          {usersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              {filteredUsers.length === 0 ? (
                <Typography sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <FormattedMessage id="collector_configure.no_users_found" defaultMessage="No users found" />
                </Typography>
              ) : (
                <List>
                  {filteredUsers.map((user, index) => {
                    const isSelected = selectedUsers.some(u => u.id === user.id);
                    const isAlreadyRecipient = emailRecipients.some(r => r.email === user.email);

                    return (
                      <ListItem
                        key={user.id}
                        button
                        onClick={(event) => handleUserSelection(user, index, event)}
                        sx={{
                          borderRadius: '8px',
                          mb: 1,
                          backgroundColor: isSelected
                            ? 'rgba(0, 67, 142, 0.1)'
                            : newlyCreatedUsers.includes(user.id)
                              ? 'rgba(76, 175, 80, 0.1)'
                              : 'transparent',
                          border: isSelected
                            ? '2px solid #00438E'
                            : newlyCreatedUsers.includes(user.id)
                              ? '2px solid #4caf50'
                              : '1px solid #e0e0e0',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: isSelected
                              ? 'rgba(0, 67, 142, 0.15)'
                              : newlyCreatedUsers.includes(user.id)
                                ? 'rgba(76, 175, 80, 0.15)'
                                : 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            checked={isSelected}
                            edge="start"
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {user.first_name} {user.last_name}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {newlyCreatedUsers.includes(user.id) && (
                                  <Chip
                                    label={<FormattedMessage id="collector_configure.newly_created" defaultMessage="New" />}
                                    size="small"
                                    color="success"
                                    sx={{
                                      backgroundColor: '#4caf50',
                                      color: 'white',
                                      fontSize: '0.7rem',
                                      height: '20px'
                                    }}
                                  />
                                )}
                                {isAlreadyRecipient && (
                                  <Chip
                                    label={<FormattedMessage id="collector_configure.already_recipient" defaultMessage="Already added" />}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {user.email}
                              </Typography>
                              {user.username && (
                                <Typography variant="body2" color="text.secondary">
                                  @{user.username}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            <FormattedMessage id="collector_configure.ctrl_shift_hint" defaultMessage="Use Ctrl+Click for multi-select, Shift+Click for range select" />
          </Typography>
          <Button onClick={handleCloseUserSelectionModal} color="primary">
            <FormattedMessage id="collector_configure.cancel" />
          </Button>
          <Button
            onClick={handleAddSelectedUsers}
            color="primary"
            variant="contained"
            disabled={selectedUsers.length === 0}
            sx={{ textTransform: 'none' }}
          >
            <FormattedMessage
              id="collector_configure.add_selected_users"
              defaultMessage="Add {count} User(s)"
              values={{ count: selectedUsers.length }}
            />
          </Button>
        </DialogActions>
      </Dialog>

      {/* New User Creation Modal */}
      <Dialog
        open={newUserModal}
        onClose={handleCloseNewUserModal}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#00438E',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <PersonAddIcon />
          <FormattedMessage id="collector_configure.create_new_user_title" defaultMessage="Create New User" />
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 2 }}>
            <FormattedMessage id="collector_configure.create_user_description" defaultMessage="Create a new user and add them to the recipients list. Email is required." />
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label={<FormattedMessage id="collector_configure.first_name" defaultMessage="First Name" />}
              value={newUserData.first_name}
              onChange={(e) => handleNewUserInputChange('first_name', e.target.value)}
              placeholder={formatMessage({ id: "collector_configure.first_name_placeholder" })}
            />
            <TextField
              fullWidth
              label={<FormattedMessage id="collector_configure.last_name" defaultMessage="Last Name" />}
              value={newUserData.last_name}
              onChange={(e) => handleNewUserInputChange('last_name', e.target.value)}
              placeholder={formatMessage({ id: "collector_configure.last_name_placeholder" })}
            />
            <TextField
              fullWidth
              label={<FormattedMessage id="collector_configure.username" defaultMessage="Username" />}
              value={newUserData.username}
              onChange={(e) => handleNewUserInputChange('username', e.target.value)}
              placeholder={formatMessage({ id: "collector_configure.username_placeholder" })}
            />
            <TextField
              fullWidth
              required
              label={<FormattedMessage id="collector_configure.email_label" />}
              value={newUserData.email}
              onChange={(e) => handleNewUserInputChange('email', e.target.value)}
              placeholder={formatMessage({ id: "collector_configure.email_placeholder" })}
              type="email"
              error={!newUserData.email}
              helperText={!newUserData.email ? <FormattedMessage id="collector_configure.email_required" defaultMessage="Email is required" /> : ""}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button onClick={handleCloseNewUserModal} color="primary" sx={{ order: { xs: 3, sm: 1 } }}>
            <FormattedMessage id="collector_configure.cancel" />
          </Button>
          <Button
            onClick={handleCreateUserOnly}
            color="primary"
            variant="outlined"
            disabled={creatingUser || !newUserData.email}
            sx={{ textTransform: 'none', order: { xs: 1, sm: 2 } }}
          >
            {creatingUser ? (
              <CircularProgress size={16} sx={{ mr: 1 }} />
            ) : (
              <PersonAddIcon sx={{ mr: 1, fontSize: 16 }} />
            )}
            <FormattedMessage id="collector_configure.create_user_only" defaultMessage="Create User Only" />
          </Button>
          <Button
            onClick={handleCreateNewUser}
            color="primary"
            variant="contained"
            disabled={creatingUser || !newUserData.email}
            sx={{ textTransform: 'none', order: { xs: 2, sm: 3 } }}
          >
            {creatingUser ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : (
              <PersonAddIcon sx={{ mr: 1, fontSize: 18 }} />
            )}
            <FormattedMessage id="collector_configure.create_and_add" defaultMessage="Create & Add to Recipients" />
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set Time and Send Modal */}
      <Dialog
        open={sendModalOpen}
        onClose={handleSendModalClose}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#00438E',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <FormattedMessage id="collector_configure.set_time_and_send" />
        </DialogTitle>
        <DialogContent sx={{ mt: 3, p: 3 }}>
          <Typography variant="body1" sx={{ mb: 1, fontWeight: '500' }}>
            <FormattedMessage id="collector_configure.send_time_title" defaultMessage="When would you like to send the invitations?" />
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                transform: 'translateX(4px)'
              },
              ...(sendTime === 'now' && {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                border: '1px solid rgba(25, 118, 210, 0.2)',
                transform: 'translateX(4px)'
              })
            }}>
              <Radio
                checked={sendTime === 'now'}
                onChange={(e) => setSendTime(e.target.value)}
                value="now"
                name="send-time"
                sx={{
                  mr: 2,
                  color: sendTime === 'now' ? '#1976d2' : 'rgba(0, 0, 0, 0.54)',
                  '&.Mui-checked': {
                    color: '#1976d2'
                  }
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  onClick={() => setSendTime('now')}
                  sx={{
                    fontSize: '1rem',
                    fontWeight: sendTime === 'now' ? '600' : '500',
                    color: sendTime === 'now' ? '#1976d2' : 'text.primary',
                    cursor: 'pointer',
                    transition: 'color 0.3s ease'
                  }}
                  variant="body1"
                >
                  <FormattedMessage id="collector_configure.send_now" defaultMessage="Send Now" />
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                  <FormattedMessage id="collector_configure.send_immediately" defaultMessage="Send invitations right away" />
                </Typography>
              </Box>
            </Box>

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                transform: 'translateX(4px)'
              },
              ...(sendTime === 'schedule' && {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                border: '1px solid rgba(25, 118, 210, 0.2)',
                transform: 'translateX(4px)'
              })
            }}>
              <Radio
                checked={sendTime === 'schedule'}
                onChange={(e) => setSendTime(e.target.value)}
                value="schedule"
                name="send-time"
                sx={{
                  mr: 2,
                  color: sendTime === 'schedule' ? '#1976d2' : 'rgba(0, 0, 0, 0.54)',
                  '&.Mui-checked': {
                    color: '#1976d2'
                  }
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  onClick={() => setSendTime('schedule')}
                  sx={{
                    fontSize: '1rem',
                    fontWeight: sendTime === 'schedule' ? '600' : '500',
                    color: sendTime === 'schedule' ? '#1976d2' : 'text.primary',
                    cursor: 'pointer',
                    transition: 'color 0.3s ease'
                  }}
                  variant="body1"
                >
                  <FormattedMessage id="collector_configure.schedule_for_later" defaultMessage="Schedule for Later" />
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                  <FormattedMessage id="collector_configure.choose_specific_time" defaultMessage="Choose when to send the invitations" />
                </Typography>
              </Box>
            </Box>
          </Box>

          {sendTime === 'schedule' && (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <ScheduleIcon sx={{ color: '#1976d2', fontSize: '1.5rem' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: '500' }}>
                  <FormattedMessage id="collector_configure.select_date_time" defaultMessage="Select the date and time to send the invitations:" />
                </Typography>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)',
                    borderRadius: '1px',
                    zIndex: 1,
                    transition: 'all 0.3s ease',
                    transform: 'scaleX(0.8)',
                    transformOrigin: 'center'
                  },
                  '&:hover::before': {
                    transform: 'scaleX(1)',
                    background: 'linear-gradient(90deg, #1565c0 0%, #1976d2 50%, #1565c0 100%)'
                  },
                  '&.Mui-focused::before': {
                    transform: 'scaleX(1.1)',
                    background: 'linear-gradient(90deg, #0d47a1 0%, #1565c0 30%, #1976d2 70%, #0d47a1 100%)',
                    height: '3px',
                    top: '-1px'
                  }
                }}
                data-datetime-container="true"
              >
                {/* Custom Styled Input Field */}
                <Box
                  sx={{
                    position: 'relative',
                    // width: '100%',
                    '& .custom-input': {
                      width: '100%',
                      padding: '16px 65px 16px 20px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      border: '2px solid transparent',
                      fontSize: '1rem',
                      fontWeight: '500',
                      color: '#2c3e50',
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        borderColor: 'rgba(25, 118, 210, 0.2)',
                        boxShadow: '0 4px 16px rgba(25, 118, 210, 0.12)',
                        transform: 'translateY(-1px)'
                      },
                      '&:focus': {
                        outline: 'none',
                        backgroundColor: 'rgba(25, 118, 210, 0.06)',
                        borderColor: '#1976d2',
                        boxShadow: '0 8px 24px rgba(25, 118, 210, 0.18)',
                        transform: 'translateY(-2px) scale(1.01)'
                      }
                    }
                  }}
                >
                  <input
                    type="text"
                    readOnly
                    value={scheduledDateTime.toLocaleString()}
                    onClick={handlePickerOpen}
                    className="custom-input"
                    placeholder={formatMessage({
                      id: 'collector_configure.select_datetime_placeholder',
                      defaultMessage: 'Select date and time...'
                    })}
                  />
                  {/* Custom Date Picker Button */}
                  <Box
                    sx={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      border: '1px solid rgba(25, 118, 210, 0.15)',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.12)',
                        borderColor: 'rgba(25, 118, 210, 0.3)',
                        transform: 'translateY(-50%) scale(1.08)',
                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.2)',
                        '& .picker-icon': {
                          color: '#1565c0',
                          transform: 'scale(1.1)'
                        }
                      },
                      '&:active': {
                        transform: 'translateY(-50%) scale(0.95)',
                        transition: 'all 0.1s ease',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
                      }
                    }}
                    onClick={handlePickerOpen}
                  >
                    <ScheduleIcon
                      className="picker-icon"
                      sx={{
                        fontSize: '1.25rem',
                        color: '#1976d2',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleSendModalClose} color="primary">
            <FormattedMessage id="collector_configure.cancel" />
          </Button>
          <Button
            onClick={() => handleSendInvitations(sendTime === 'schedule', sendTime === 'schedule' ? toLocalISOString(scheduledDateTime) : null)}
            color="primary"
            variant="contained"
            disabled={loading || (sendTime === 'schedule' && scheduledDateTime <= new Date())}
          >
            {sendTime === 'schedule' ? (
              <FormattedMessage id="collector_configure.schedule_send" defaultMessage="Schedule Send" />
            ) : (
              <FormattedMessage id="collector_configure.send_now_button" defaultMessage="Send Now" />
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reminder Modal */}
      <Dialog
        open={reminderModalOpen}
        onClose={handleReminderModalClose}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            minHeight: '600px'
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#00438E',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <FormattedMessage id="collector_configure.send_reminder" defaultMessage="Send Reminder" />
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 3 }}>
          <Typography variant="body1" sx={{ mb: 3, fontWeight: '500' }}>
            <FormattedMessage id="collector_configure.reminder_options" defaultMessage="Choose who should receive the reminder:" />
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                transform: 'translateX(4px)'
              },
              ...(reminderType === 'completed' && {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                border: '1px solid rgba(25, 118, 210, 0.2)',
                transform: 'translateX(4px)'
              })
            }}>
              <Radio
                checked={reminderType === 'completed'}
                onChange={(e) => {
                  setReminderType(e.target.value);
                  setReminderMessage(getDefaultReminderMessage(e.target.value));
                }}
                value="completed"
                name="reminder-type"
                sx={{
                  mr: 2,
                  color: reminderType === 'completed' ? '#1976d2' : 'rgba(0, 0, 0, 0.54)',
                  '&.Mui-checked': {
                    color: '#1976d2'
                  }
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  onClick={() => setReminderType('completed')}
                  sx={{
                    fontSize: '1rem',
                    fontWeight: reminderType === 'completed' ? '600' : '500',
                    color: reminderType === 'completed' ? '#1976d2' : 'text.primary',
                    cursor: 'pointer',
                    transition: 'color 0.3s ease'
                  }}
                  variant="body1"
                >
                  <FormattedMessage id="collector_configure.reminder_completed" defaultMessage="Send reminder to those who have completed the assessment" />
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                  <FormattedMessage id="collector_configure.reminder_completed_desc" defaultMessage="Send a follow-up reminder to participants who have already completed the assessment" />
                </Typography>
              </Box>
            </Box>

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                transform: 'translateX(4px)'
              },
              ...(reminderType === 'note' && {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                border: '1px solid rgba(25, 118, 210, 0.2)',
                transform: 'translateX(4px)'
              })
            }}>
              <Radio
                checked={reminderType === 'note'}
                onChange={(e) => {
                  setReminderType(e.target.value);
                  setReminderMessage(getDefaultReminderMessage(e.target.value));
                }}
                value="note"
                name="reminder-type"
                sx={{
                  mr: 2,
                  color: reminderType === 'note' ? '#1976d2' : 'rgba(0, 0, 0, 0.54)',
                  '&.Mui-checked': {
                    color: '#1976d2'
                  }
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  onClick={() => setReminderType('note')}
                  sx={{
                    fontSize: '1rem',
                    fontWeight: reminderType === 'note' ? '600' : '500',
                    color: reminderType === 'note' ? '#1976d2' : 'text.primary',
                    cursor: 'pointer',
                    transition: 'color 0.3s ease'
                  }}
                  variant="body1"
                >
                  <FormattedMessage id="collector_configure.send_note_completed" defaultMessage="Send a note to those who have completed the assessment" />
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                  <FormattedMessage id="collector_configure.send_note_desc" defaultMessage="Send a thank you note or additional message to participants who completed the assessment" />
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* ReactQuill Editor */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: '500', color: 'text.secondary' }}>
              <FormattedMessage id="collector_configure.customize_message" defaultMessage="Customize your message:" />
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              <FormattedMessage id="collector_configure.message_description" defaultMessage="Customize your message using the editor below. You can use rich text formatting and the following placeholders:" />
            </Typography>
            <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(25, 118, 210, 0.05)', borderRadius: '8px', border: '1px solid rgba(25, 118, 210, 0.1)' }}>
              <Typography variant="caption" sx={{ fontWeight: '600', color: '#1976d2', mb: 1, display: 'block' }}>
                <FormattedMessage id="collector_configure.available_placeholders" defaultMessage="Available Placeholders:" />
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                • <code style={{ backgroundColor: 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: '3px' }}>$recipient_name$</code> - Recipient's name
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                • <code style={{ backgroundColor: 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: '3px' }}>$survey_link$</code> - Direct link to the assessment
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                • <code style={{ backgroundColor: 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: '3px' }}>$survey_name$</code> - Name of the assessment
              </Typography>
            </Box>

            <Box sx={{
              '& .ql-toolbar': {
                borderTop: '1px solid #e0e0e0',
                borderLeft: '1px solid #e0e0e0',
                borderRight: '1px solid #e0e0e0',
                borderBottom: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: '#f8f9fa',
                '& .ql-picker': {
                  color: '#1976d2'
                },
                '& button': {
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    '& .ql-stroke': {
                      stroke: '#1976d2'
                    },
                    '& .ql-fill': {
                      fill: '#1976d2'
                    }
                  }
                }
              },
              '& .ql-container': {
                border: '1px solid #e0e0e0',
                borderRadius: '0 0 8px 8px',
                fontSize: '1rem',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                '& .ql-editor': {
                  minHeight: '200px',
                  padding: '16px 20px',
                  lineHeight: '1.6',
                  '& p': {
                    marginBottom: '1rem'
                  },
                  '& strong': {
                    fontWeight: '600'
                  },
                  '& em': {
                    fontStyle: 'italic'
                  },
                  '& u': {
                    textDecoration: 'underline'
                  }
                }
              },
              '& .ql-editor.ql-blank::before': {
                color: '#9e9e9e',
                fontStyle: 'normal',
                fontSize: '1rem'
              }
            }}>
              <ReactQuill
                value={reminderMessage}
                onChange={setReminderMessage}
                placeholder={formatMessage({
                  id: 'collector_configure.message_placeholder',
                  defaultMessage: 'Click here to edit the message... You can customize this template with your own content.'
                })}
                modules={fullToolbarConfig}
                formats={[
                  "bold",
                  "italic",
                  "underline",
                  "color",
                  "size",
                  "lineheight",
                  "letterspacing",
                  "align",
                  "link",
                  "list",
                  "bullet",
                ]}
                style={{
                  backgroundColor: "#ffffff",
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleReminderModalClose} color="primary">
            <FormattedMessage id="collector_configure.cancel" />
          </Button>
          <Button
            onClick={handleReminderModalClose}
            color="primary"
            variant="contained"
            disabled={loading || !reminderMessage.trim()}
          >
            <FormattedMessage id="collector_configure.send_reminder_button" defaultMessage="Send Reminder" />
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Date Time Picker Popover */}
      <Popover
        open={pickerOpen}
        anchorEl={pickerAnchorEl}
        onClose={handlePickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(25, 118, 210, 0.15)',
            border: '1px solid rgba(25, 118, 210, 0.1)',
            overflow: 'hidden',
            minWidth: '320px'
          }
        }}
      >
        <ClickAwayListener onClickAway={handlePickerClose}>
          <Box sx={{ p: 0 }}>
            {!timeView ? (
              /* Calendar View */
              <Box sx={{ p: 3, backgroundColor: '#fff' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <IconButton
                    onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1))}
                    size="small"
                    sx={{ color: '#1976d2' }}
                  >
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                  <Typography variant="h6" sx={{ fontWeight: '600', color: '#1976d2' }}>
                    {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </Typography>
                  <IconButton
                    onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1))}
                    size="small"
                    sx={{ color: '#1976d2' }}
                  >
                    <KeyboardArrowRightIcon />
                  </IconButton>
                </Box>

                {/* Days of Week Header */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 2 }}>
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                    <Typography
                      key={day}
                      variant="caption"
                      sx={{
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#666',
                        fontSize: '0.75rem'
                      }}
                    >
                      {day}
                    </Typography>
                  ))}
                </Box>

                {/* Calendar Days */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                  {getMonthDays(selectedDate).map((date, index) => {
                    const isCurrentMonth = date.getMonth() === selectedDate.getMonth();
                    const isSelected = date.toDateString() === selectedDate.toDateString();
                    const isToday = date.toDateString() === new Date().toDateString();
                    const isPast = date < new Date().setHours(0, 0, 0, 0);

                    return (
                      <Box
                        key={index}
                        onClick={() => !isPast && handleDateSelect(date)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '36px',
                          borderRadius: '8px',
                          cursor: isPast || !isCurrentMonth ? 'not-allowed' : 'pointer',
                          backgroundColor: isSelected
                            ? '#1976d2'
                            : isToday && isCurrentMonth
                              ? 'rgba(25, 118, 210, 0.1)'
                              : 'transparent',
                          color: isSelected
                            ? '#fff'
                            : isPast || !isCurrentMonth
                              ? '#ccc'
                              : isToday && isCurrentMonth
                                ? '#1976d2'
                                : '#333',
                          fontWeight: isSelected || (isToday && isCurrentMonth) ? '600' : '400',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: isSelected
                              ? '#1565c0'
                              : isCurrentMonth && !isPast
                                ? 'rgba(25, 118, 210, 0.08)'
                                : 'transparent',
                            transform: isCurrentMonth && !isPast ? 'scale(1.05)' : 'scale(1)'
                          }
                        }}
                      >
                        {date.getDate()}
                      </Box>
                    );
                  })}
                </Box>

                {/* Today Button */}
                <Button
                  onClick={() => handleDateSelect(new Date())}
                  fullWidth
                  variant="outlined"
                  sx={{
                    mt: 2,
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    borderRadius: '8px',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#1565c0',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                  startIcon={<TodayIcon />}
                >
                  {formatMessage({ id: 'collector_configure.today', defaultMessage: 'Today' })}
                </Button>
              </Box>
            ) : (
              /* Time Selection View — hour grid + minute grid */
              <Box sx={{ p: 3, backgroundColor: '#fff', minWidth: '360px' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <IconButton
                    onClick={() => setTimeView(false)}
                    size="small"
                    sx={{ color: '#1976d2' }}
                  >
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                  <Typography variant="h6" sx={{ fontWeight: '600', color: '#1976d2' }}>
                    {formatMessage({ id: 'collector_configure.select_time', defaultMessage: 'Select Time' })}
                  </Typography>
                  <Box sx={{ width: '40px' }} />
                </Box>

                {/* Live time display */}
                <Box
                  sx={{
                    textAlign: 'center',
                    mb: 3,
                    py: 1.5,
                    background: 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(66,165,245,0.06) 100%)',
                    borderRadius: '12px',
                    border: '1px solid rgba(25,118,210,0.15)',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '2.25rem',
                      fontWeight: 700,
                      color: '#1565c0',
                      letterSpacing: '0.12em',
                      fontFamily: '"Roboto Mono", "Courier New", monospace',
                      lineHeight: 1,
                    }}
                  >
                    {selectedTime}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </Typography>
                </Box>

                {/* Hour grid */}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    display: 'block',
                    mb: 1,
                  }}
                >
                  {formatMessage({ id: 'collector_configure.hour', defaultMessage: 'Hour' })}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.75, mb: 2.5 }}>
                  {Array.from({ length: 24 }, (_, i) => {
                    const hStr = i.toString().padStart(2, '0');
                    const isSelected = hStr === selectedTime.split(':')[0];
                    return (
                      <Box
                        key={i}
                        onClick={() => setSelectedTime(`${hStr}:${selectedTime.split(':')[1]}`)}
                        sx={{
                          textAlign: 'center',
                          py: 0.8,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.82rem',
                          fontWeight: isSelected ? 700 : 400,
                          bgcolor: isSelected ? '#1976d2' : 'rgba(25,118,210,0.05)',
                          color: isSelected ? '#fff' : '#444',
                          border: '1px solid',
                          borderColor: isSelected ? '#1565c0' : 'rgba(25,118,210,0.1)',
                          transition: 'all 0.15s ease',
                          userSelect: 'none',
                          '&:hover': {
                            bgcolor: isSelected ? '#1565c0' : 'rgba(25,118,210,0.14)',
                            borderColor: '#1976d2',
                            transform: 'scale(1.08)',
                          },
                        }}
                      >
                        {hStr}
                      </Box>
                    );
                  })}
                </Box>

                {/* Minute grid */}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    display: 'block',
                    mb: 1,
                  }}
                >
                  {formatMessage({ id: 'collector_configure.minute', defaultMessage: 'Minute' })}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.75, mb: 3 }}>
                  {Array.from({ length: 12 }, (_, i) => {
                    const mStr = (i * 5).toString().padStart(2, '0');
                    const isSelected = mStr === selectedTime.split(':')[1];
                    return (
                      <Box
                        key={i}
                        onClick={() => setSelectedTime(`${selectedTime.split(':')[0]}:${mStr}`)}
                        sx={{
                          textAlign: 'center',
                          py: 0.8,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.82rem',
                          fontWeight: isSelected ? 700 : 400,
                          bgcolor: isSelected ? '#1976d2' : 'rgba(25,118,210,0.05)',
                          color: isSelected ? '#fff' : '#444',
                          border: '1px solid',
                          borderColor: isSelected ? '#1565c0' : 'rgba(25,118,210,0.1)',
                          transition: 'all 0.15s ease',
                          userSelect: 'none',
                          '&:hover': {
                            bgcolor: isSelected ? '#1565c0' : 'rgba(25,118,210,0.14)',
                            borderColor: '#1976d2',
                            transform: 'scale(1.08)',
                          },
                        }}
                      >
                        {mStr}
                      </Box>
                    );
                  })}
                </Box>

                {/* Confirm */}
                <Button
                  onClick={handlePickerConfirm}
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: '#1976d2',
                    borderRadius: '10px',
                    textTransform: 'none',
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    '&:hover': { backgroundColor: '#1565c0' },
                  }}
                >
                  {formatMessage({ id: 'collector_configure.confirm_time', defaultMessage: 'Confirm Time' })}
                </Button>
              </Box>
            )}
          </Box>
        </ClickAwayListener>
      </Popover>
    </>
  );
}

export default CollectorConfigure;
